import type {ComponentStageData} from '../../../../types'
import type {StackItemLayout} from '@wix/document-services-types'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer
} from '../../../../conversionDal/utils/pointer'
import {isStackContainerLayout, updateTargetLayout} from '../../../common'
import {HORIZONTAL_STACK_WIDTH_THRESHOLD} from '../../constants'

const getFinalDirection = (
    conversionDal: ComponentStageData['conversionDal'],
    componentPointer: ComponentStageData['componentPointer']
): 'row' | 'column' => {
    const sourceContainerLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))?.containerLayout
    const initialDirection: 'row' | 'column' = sourceContainerLayout?.direction ?? 'column'
    const containerWidth: number = conversionDal.get(getMeasurementsPointer(componentPointer))?.width ?? 0
    return initialDirection === 'row' && containerWidth <= HORIZONTAL_STACK_WIDTH_THRESHOLD ? 'row' : 'column'
}

export const createOrderRealStructure = ({componentPointer, conversionDal, initialSnapshot}: ComponentStageData) => {
    const component = conversionDal.get(componentPointer)

    if (!component?.components?.length || component.type !== 'Container' || component.isSlottedComponent) {
        return
    }

    if (!isStackContainerLayout({initialSnapshot, componentPointer})) {
        return
    }

    conversionDal.modify(componentPointer, value => ({
        ...value,
        components: [...value.components].sort((a, b) => {
            const orderA =
                (conversionDal.get(getSourceLayoutPointer({id: a}))?.itemLayout as StackItemLayout)?.order ?? 0
            const orderB =
                (conversionDal.get(getSourceLayoutPointer({id: b}))?.itemLayout as StackItemLayout)?.order ?? 0
            return orderA - orderB
        })
    }))
}

export const positionItems = ({componentPointer, conversionDal, initialSnapshot}: ComponentStageData) => {
    const component = conversionDal.get(componentPointer)

    if (!component?.components?.length || component.type !== 'Container' || component.isSlottedComponent) {
        return
    }

    if (!isStackContainerLayout({initialSnapshot, componentPointer})) {
        return
    }

    const finalDirection = getFinalDirection(conversionDal, componentPointer)

    updateTargetLayout(
        {conversionDal, componentPointer},
        {
            containerLayout: {
                type: 'StackContainerLayout',
                direction: finalDirection,
                rowGap: {value: 0, type: 'px'},
                columnGap: {value: 0, type: 'px'}
            }
        }
    )

    component.components.forEach((childId: string, index: number) => {
        const childPointer = getStructurePointer({id: childId})
        updateTargetLayout(
            {conversionDal, componentPointer: childPointer},
            {
                itemLayout: {
                    type: 'StackItemLayout',
                    order: index,
                    alignSelf: 'start'
                }
            }
        )
    })
}
